import type { NextPage } from 'next'
import { DefaultSeo } from 'next-seo'
import { IntercomProvider } from 'react-use-intercom'

import { ApolloProvider } from '@apollo/client'
import { config } from '@fortawesome/fontawesome-svg-core'
import { i18n } from '@lingui/core'
import { I18nProvider } from '@lingui/react'
import AuthorizationsContext from 'Root/App/Contexts/Authorizations'
import NoteContext from 'Root/App/Contexts/Note'
import RouterHistory from 'Root/Contexts/RouterHistory'
import TokenContext from 'Root/Contexts/Token'
import Middlewares from 'Root/Middlewares'
import CollectionContext from 'Root/Pages/Collection/Component/Context'
import PageLayout from 'Root/Pages/Layout'
import apolloClient from 'lib/apollo-client'
import { POLICY_LANG_CONFIG } from 'lib/constants'
import getQueryString from 'lib/get-query-string'
import { useLinguiInit } from 'lib/i18n'
import type { AppProps } from 'next/app'
import { useRouter } from 'next/router'
import Script from 'next/script'

import SEO from '../../next-seo.config'

import '@egjs/flicking-plugins/dist/arrow.css'
import '@egjs/flicking-plugins/dist/pagination.css'
import '@egjs/react-flicking/dist/flicking.css'
import '@fortawesome/fontawesome-svg-core/styles.css'
import 'Root/Component/RichTextEditor/globals.css'
import 'react-pdf/dist/esm/Page/AnnotationLayer.css'
import 'react-sliding-pane/dist/react-sliding-pane.css'
import 'styles/globals.css'
import 'styles/shared.css'
import 'tailwindcss/tailwind.css'

config.autoAddCss = false

const iubendacsContentJapanese =
  "<div class='iubenda-banner-content iubenda-custom-content' role='document'><div id='iubenda-cs-paragraph' class='iubenda-cs-small-margin-top'><p class='iub-p'>私たち および選択された第三者（12）は、<a href='https://www.iubenda.com/privacy-policy/36132119/cookie-policy?an=no&amp;s_ck=false&amp;newmarkup=yes' role='button' target='_blank' rel='noopener' class='iubenda-cs-cookie-policy-lnk'>プライバシーポリシー</a> に記載されているとおり、個人情報を収集し、技術的な目的で、またお客様の同意のもと、体験の向上、測定、および「マーケティング（パーソナライズ広告）」の目的で、クッキーまたは類似の技術を使用します。詳細は <a href='https://www.iubenda.com/terms-and-conditions/13152455' role='button' target='_blank' rel='noopener' class='iubenda-cs-cookie-policy-lnk'>利用規約</a> をご覧ください。</p><p class='iub-p'>同意は、設定パネルにアクセスすることで、いつでも自由に提供、拒否、または撤回することができます。同意を拒否した場合、一部の機能が利用できなくなる可能性があります。「同意する」ボタンを使用して同意してください。「拒否する」ボタンを使用すると、同意せずに続行できます。</p></div></div>"

const getIubendaPolicyConfig = (locale) =>
  JSON.stringify({
    askConsentAtCookiePolicyUpdate: true,
    cookiePolicyInOtherWindow: true,
    enableFadp: true,
    enableLgpd: true,
    enableUspr: false,
    fadpApplies: true,
    floatingPreferencesButtonDisplay: false,
    perPurposeConsent: true,
    preferenceCookie: { expireAfter: 180 },
    siteId: 3714938,
    usprApplies: true,
    usPreferencesWidgetDisplay: 'inline-center',
    whitelabel: false,
    cookiePolicyId: POLICY_LANG_CONFIG[locale !== 'ja' ? locale : 'en'],
    lang: locale !== 'ja' ? locale : 'en',
    banner: {
      acceptButtonColor: '#9489FA',
      acceptButtonDisplay: true,
      backgroundColor: '#111827',
      backgroundOverlay: true,
      closeButtonDisplay: false,
      content: locale === 'ja' ? iubendacsContentJapanese : null,
      html:
        locale === 'ja'
          ? "<div class='iubenda-cs-container' role='region'>" +
            "<div class='iubenda-cs-content' style='background-color: #111827 !important;color: #fff !important;font-size: 14px !important;'>" +
            "<div class='iubenda-cs-rationale'>" +
            '<div>%{banner_content}</div>' +
            "<div class='iubenda-cs-opt-group' style='color:#111827!important;'>" +
            "<div class='iubenda-cs-opt-group-custom'>" +
            "<button class='iubenda-cs-customize-btn' tabindex='0' role='button'>設定を管理する</button>" +
            '</div>' +
            "<div class='iubenda-cs-opt-group-consent'>" +
            "<button class='iubenda-cs-reject-btn iubenda-cs-btn-primary' tabindex='0' role='button'>拒否</button>" +
            "<button class='iubenda-cs-accept-btn iubenda-cs-btn-primary' tabindex='0' role='button'>同意</button>" +
            '</div>' +
            '</div>' +
            '</div>' +
            '</div>'
          : null,
      continueWithoutAcceptingButtonColor: '#1F2937',
      continueWithoutAcceptingButtonDisplay: false,
      customizeButtonColor: '#1F2937',
      customizeButtonDisplay: true,
      explicitWithdrawal: true,
      listPurposes: true,
      logo: null,
      position: 'bottom',
      rejectButtonColor: '#1F2937',
      rejectButtonDisplay: true,
      showTitle: false,
      showTotalNumberOfProviders: true,
      zIndex: 2147483004,
      enableRemoteConsent: true,
      remoteConsentOrigin: 'https://taylora.com',
      autoblocking: true,
    },
  })

const App: NextPage<AppProps> = ({ Component, pageProps }: AppProps) => {
  const { query } = useRouter()
  useLinguiInit(pageProps.translation)

  return (
    <TokenContext>
      <ApolloProvider client={apolloClient}>
        <RouterHistory>
          <CollectionContext>
            <I18nProvider i18n={i18n}>
              <AuthorizationsContext>
                <NoteContext>
                  <Middlewares>
                    <DefaultSeo {...SEO} />
                    <IntercomProvider
                      appId={process.env.NEXT_PUBLIC_INTERCOM_APP_ID}
                    >
                      <PageLayout workspace={pageProps.workspace}>
                        <Component {...pageProps} />
                      </PageLayout>
                    </IntercomProvider>

                    <Script
                      src={`https://www.googletagmanager.com/gtag/js?id=${process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_4}`}
                    />

                    <Script id="iubenda-config">
                      {`var _iub = _iub || [];
                        _iub.csConfiguration = ${getIubendaPolicyConfig(
                          getQueryString(query.locale)
                        )};`}
                    </Script>

                    <Script
                      id="iubenda-blocking"
                      src="https://cs.iubenda.com/autoblocking/3714938.js"
                    />
                    <Script
                      id="iubenda-stub"
                      src="//cdn.iubenda.com/cs/gpp/stub.js"
                    />

                    <Script
                      id="iubenda-cs"
                      src="//cdn.iubenda.com/cs/iubenda_cs.js"
                    />
                  </Middlewares>
                </NoteContext>
              </AuthorizationsContext>
            </I18nProvider>
          </CollectionContext>
        </RouterHistory>
      </ApolloProvider>
    </TokenContext>
  )
}

export default App
